import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  viewChild,
} from '@angular/core'

import { Chart, ChartConfiguration } from 'chart.js/auto'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { ChartDataset, ChartProps, ChartTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getComputedValueSignal, getDynamicPropSignal } from '../helpers'

const DEFAULT_PALETTE: string[] = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']

interface EffectiveChartData {
  labels: string[]
  datasets: ChartDataset[]
}

const buildChartConfig = (
  flowxProps: ChartProps,
  title: string,
  data: EffectiveChartData
): ChartConfiguration => ({
  type: flowxProps.type ?? 'bar',
  data: {
    labels: data.labels ?? [],
    datasets: (data.datasets ?? []).map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data,
      backgroundColor: dataset.color ?? DEFAULT_PALETTE[index % DEFAULT_PALETTE.length],
      borderColor: dataset.color ?? DEFAULT_PALETTE[index % DEFAULT_PALETTE.length],
    })),
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: flowxProps.showLegend ?? true,
      },
      title: {
        display: !!title,
        text: title ?? '',
      },
    },
  },
})

@Component({
  selector: 'flx-chart-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './chart-wrapper.component.html',
  styleUrl: './chart-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxChartWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly #injector = inject(Injector)
  readonly #ngZone = inject(NgZone)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  #chart: Chart | null = null

  readonly canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas')

  templateConfig = input.required<ChartTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  readonly isDynamicDataSource = computed(() => !!this.flowxProps().isDynamicDataSource)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  title: Signal<string>
  labelsSource: Signal<string[]>
  datasetsSource: Signal<ChartDataset[]>

  readonly effectiveData = computed(
    (): EffectiveChartData =>
      this.isDynamicDataSource()
        ? { labels: this.labelsSource(), datasets: this.datasetsSource() }
        : { labels: this.flowxProps().labels, datasets: this.flowxProps().datasets }
  )

  readonly hostClass = computed(() => [this.displayOptions().className].filter(Boolean).join(' '))

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      this.title = getDynamicPropSignal(props.title || '', templateConfig)
      // `getComputedValueSignal` evaluates the expression via `new Function` and preserves the
      // raw resolved value (array), unlike `getDynamicPropSignal` which stringifies via text
      // interpolation — required to feed chart.js with actual arrays instead of JSON strings.
      this.labelsSource = getComputedValueSignal(
        props.labelsSource || '[]',
        templateConfig
      ) as unknown as Signal<string[]>
      this.datasetsSource = getComputedValueSignal(
        props.datasetsSource || '[]',
        templateConfig
      ) as unknown as Signal<ChartDataset[]>
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })

      effect(() => {
        const isHidden = this.isHidden()
        const canvasEl = this.canvas()?.nativeElement
        const props = this.flowxProps()
        const title = this.title()
        const data = this.effectiveData()

        if (typeof window === 'undefined' || isHidden || !canvasEl) {
          return
        }

        this.#ngZone.runOutsideAngular(() => {
          this.#chart?.destroy()
          this.#chart = new Chart(canvasEl, buildChartConfig(props, title, data))
        })
      })
    })
  }

  ngOnDestroy(): void {
    this.#chart?.destroy()
    this.#chart = null
  }
}
